import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { 
  RegistrationRequest, 
  RegistrationRequestSummary,
} from '@/types/registrationRequest';

export class RegistrationRequestService {
  // Get all registration requests with minimal details (for overview/list views)
  static async getAllRegistrationRequests(
    page: number = 1,
    limit: number = 10,
    status?: "pending" | "approved" | "rejected",
    userType?: "dealer" | "business",
    search?: string
  ): Promise<{
    requests: RegistrationRequestSummary[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    
    // Add role filter if userType is provided (backend expects 'dealer' or 'business')
    if (userType && (userType === 'dealer' || userType === 'business')) {
      params.role = userType;
    }
    
    // Add status filter if provided
    if (status) {
      params.status = status;
    }
    
    // Add search parameter if provided
    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await api.get<{
      success: boolean;
      count: number;
      total: number;
      pagination: {
        page: number;
        limit: number;
        pages: number;
      };
      data: RegistrationRequestSummary[];
    }>('/admin/registration-applications', { params });

    // Backend already returns data in the correct format, no mapping needed
    const requests: RegistrationRequestSummary[] = response.data;

    return {
      requests,
      totalCount: response.total || 0,
      totalPages: response.pagination?.pages || 0,
      currentPage: response.pagination?.page || page,
    };
  }

  // Get registration request details by ID (for detailed views)
  static async getRegistrationRequestById(
    requestId: string
  ): Promise<RegistrationRequest> {
    const response = await api.get<{
      success: boolean;
      data: RegistrationRequest;
    }>(`/admin/registration-applications/${requestId}`);

    // Backend already returns data in the correct format, no mapping needed
    return response.data;
  }

  // Approve registration request
  static async approveRegistrationRequest(
    requestId: string,
    reviewNotes?: string
  ): Promise<RegistrationRequest> {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: RegistrationRequest;
    }>(`/admin/registration-applications/${requestId}/approve`, {
      notes: reviewNotes,
    });

    // Backend already returns data in the correct format, no mapping needed
    return response.data;
  }

  // Reject registration request
  static async rejectRegistrationRequest(
    requestId: string,
    rejectionReason: string,
    reviewNotes?: string
  ): Promise<RegistrationRequest> {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: RegistrationRequest;
    }>(`/admin/registration-applications/${requestId}/reject`, {
      reason: rejectionReason,
      notes: reviewNotes,
    });

    // Backend already returns data in the correct format, no mapping needed
    return response.data;
  }

  // Get registration application statistics
  static async getRegistrationApplicationStats(): Promise<{
    pending: { total: number; dealers: number; business: number };
    approved: number;
    rejected: number;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        pending: { total: number; dealers: number; business: number };
        approved: number;
        rejected: number;
      };
    }>('/admin/registration-applications/stats');

    return response.data;
  }

  // Bulk approve registration applications
  static async bulkApproveRegistrationApplications(
    requestIds: string[],
    reviewNotes?: string
  ): Promise<{
    approvedCount: number;
    approvedUsers: Array<{ id: string; username: string; email: string; role: string }>;
  }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: {
        approvedCount: number;
        approvedUsers: Array<{ id: string; username: string; email: string; role: string }>;
      };
    }>('/admin/registration-applications/bulk-approve', {
      userIds: requestIds,
      notes: reviewNotes,
    });

    return response.data;
  }
}